"use client";

import React, { useState } from "react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const competencies = [{
  id: "service-1",
  title: "The 700 Club Builder",
  description: "A high-velocity personal credit restoration program designed to repair, rebuild, and optimize your profile. We focus on aggressive dispute strategies and strategic account seasoning to hit your target score.",
  specs: "Comprehensive credit audit, 24/7 bureau monitoring, personalized debt-to-income ratio optimization plans.",
  paperTypes: "Direct access to credit mentors, monthly progress reports, and identity theft protection layers.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1666336/img/credit_score_growth.png",
  link: "/CreditSolutionsPage"
}, {
  id: "service-2",
  title: "Corporate Credit Launchpad",
  description: "Establish a powerful business credit profile that is entirely separate from your personal SSN. We help you build a robust Paydex score and secure high-limit vendor lines of credit.",
  specs: "EIN-based credit building, DUNS number optimization, Tier 1 through Tier 4 vendor sequence management.",
  paperTypes: "Business entity compliance check, trade line reporting verification, and corporate funding readiness.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1666336/img/corporate_funding_vault.png",
  link: "/CreditSolutionsPage"
}, {
  id: "service-3",
  title: "Strategic Business Consulting",
  description: "Scale your enterprise with expert guidance on capital structure and operational efficiency. We bridge the gap between your current revenue and your ultimate scaling potential.",
  specs: "Market analysis, cash flow forecasting, and structural reorganization for maximum tax efficiency.",
  paperTypes: "Custom growth roadmaps, quarterly performance reviews, and executive leadership coaching.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1666336/img/business_consulting_meeting.png",
  link: "/TheLakeStoneStoryPage"
}, {
  id: "service-4",
  title: "High-Limit Funding Access",
  description: "Get the funding you deserve. We leverage our network of lenders to secure liquid capital, stacks of cash, and premium credit cards for your business expansion.",
  specs: "Unsecured business lines of credit, SBA loan preparation, and private equity networking.",
  paperTypes: "Funding application management, lender-specific profile matching, and term sheet negotiation.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1666336/img/cash_stacks_cards.png",
  link: "/ContactPage"
}, {
  id: "service-5",
  title: "Wealth Management & Scaling",
  description: "Long-term asset protection and credit maintenance. We ensure your financial foundation remains solid while your capital remains fluid and productive.",
  specs: "Portfolio diversification strategies, credit limit increase automation, and risk mitigation protocols.",
  paperTypes: "Asset protection trusts, legacy planning, and continuous credit profile monitoring.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1666336/img/wealth_management_luxury.png",
  link: "/TheLakeStoneStoryPage"
}];
export default function OurCompetencies() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="233577" id="our-credit-programs" className="relative bg-background py-24 md:py-32 overflow-hidden border-b border-border">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {competencies.map((item, i) => <div key={item.id} data-index={i} className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${hoveredId === item.id ? "opacity-10 md:opacity-20" : "opacity-0"}`}>
            <img src={item.image} alt="" className="w-full h-full object-cover grayscale mix-blend-overlay" />
          </div>)}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="mb-16 md:mb-24">
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-default font-bold tracking-tighter text-foreground">
              Our Credit Programs
            </h2>
            <div className="w-24 h-1 bg-primary mt-8"></div>
          </div>

          <Accordion type="single" collapsible className="w-full border-t border-border/40">
            {competencies.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="border-border/40 group" onMouseEnter={() => setHoveredId(item.id)} onMouseLeave={() => setHoveredId(null)}>
                <AccordionTrigger className="py-8 md:py-10 hover:no-underline transition-colors [&[data-state=open]>span]:text-primary">
                  <span className="text-2xl md:text-4xl lg:text-5xl font-default font-bold text-left group-hover:translate-x-4 transition-all duration-500 text-foreground">
                    {item.title}
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 pb-10 pt-4">
                    <div className="lg:col-span-5">
                      <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-default">
                        {item.description}
                      </p>
                    </div>
                    <div className="lg:col-span-4 space-y-8">
                      <div>
                        <h4 className="text-xs font-bold tracking-widest uppercase text-primary mb-3 font-default">
                          Program Specifications
                        </h4>
                        <p className="text-foreground font-default leading-relaxed">
                          {item.specs}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-xs font-bold tracking-widest uppercase text-primary mb-3 font-default">
                          Exclusive Deliverables
                        </h4>
                        <p className="text-foreground font-default leading-relaxed">
                          {item.paperTypes}
                        </p>
                      </div>
                    </div>
                    <div className="lg:col-span-3 flex items-start lg:justify-end pt-4 lg:pt-0">
                      <Button asChild variant="outline" size="lg" className="rounded-md border-primary/50 hover:bg-primary hover:text-primary-foreground transition-all duration-300 w-full lg:w-auto group/btn shadow-md hover:shadow-primary/20">
                        <Link to={item.link}>
                          Inquire Now
                          <ArrowRight className="ml-3 size-4 group-hover/btn:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}